import React, { useState } from "react";
import BankBranchSearch from "../../BankBranchSearch";

interface Step2Props {
	formData: {
		bankName: string;
		branchName: string;
		accountType: string;
		accountNumber: string;
		accountHolderName: string;
	};
	handleChange: (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
	) => void;
	nextStep: () => void;
	prevStep: () => void;
}

const Step2: React.FC<Step2Props> = ({
	formData,
	handleChange,
	nextStep,
	prevStep,
}) => {
	const [selectedBankCode, setSelectedBankCode] = useState("");
	const [selectedBankName, setSelectedBankName] = useState(formData.bankName);
	const [selectedBranchCode, setSelectedBranchCode] = useState("");
	const [selectedBranchName, setSelectedBranchName] = useState(
		formData.branchName
	);
	const [accountHolderNameError, setAccountHolderNameError] = useState("");

	const handleBankSelect = (bank: { code: string; name: string }) => {
		setSelectedBankCode(bank.code);
		setSelectedBankName(bank.name);

		// Reset branch selection first
		setSelectedBranchCode("");
		setSelectedBranchName("");

		// Update parent form data using functional update
		handleChange({
			target: {
				name: "bankName",
				value: bank.name,
				type: "text",
			},
		} as React.ChangeEvent<HTMLInputElement>);

		handleChange({
			target: {
				name: "branchName",
				value: "",
				type: "text",
			},
		} as React.ChangeEvent<HTMLInputElement>);
	};

	const handleBranchSelect = (branch: { code: string; name: string }) => {
		setSelectedBranchCode(branch.code);
		setSelectedBranchName(branch.name);

		// Update parent form data
		const event = {
			target: {
				name: "branchName",
				value: branch.name,
				type: "text",
			},
		} as React.ChangeEvent<HTMLInputElement>;
		handleChange(event);
	};

	const handleNextStep = () => {
		setAccountHolderNameError("");

		// 必須フィールドのチェック
		if (
			!formData.bankName ||
			!formData.branchName ||
			!formData.accountType ||
			!formData.accountNumber ||
			!formData.accountHolderName
		) {
			alert("すべての必須項目を入力してください");
			return;
		}

		// カタカナのバリデーション
		const katakanaRegex = /^[ァ-ヶー\s]+$/;
		if (!katakanaRegex.test(formData.accountHolderName)) {
			setAccountHolderNameError("口座名義人はカタカナで入力してください");
			alert("口座名義人はカタカナで入力してください");
			return;
		}

		nextStep();
	};

	return (
		<div className="space-y-6">
			<div className="mb-6">
				<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
					振込先口座情報
				</h3>
				<p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
					イベントチケットの売上を振り込むための口座情報を入力してください。
				</p>
			</div>

			{/* Bank and Branch Search */}
			<BankBranchSearch
				onBankSelect={handleBankSelect}
				onBranchSelect={handleBranchSelect}
				selectedBankCode={selectedBankCode}
				selectedBankName={selectedBankName}
				selectedBranchCode={selectedBranchCode}
				selectedBranchName={selectedBranchName}
			/>

			{/* Account Type and Number */}
			<div className="flex flex-col md:flex-row md:space-x-4 space-y-6 md:space-y-0">
				<div className="w-full md:w-1/3">
					<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
						口座種別 <span className="text-red-600 dark:text-red-400">*</span>
					</label>
					<select
						name="accountType"
						value={formData.accountType}
						onChange={handleChange}
						className="w-full px-3 sm:px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400"
						required
					>
						<option value="">選択してください</option>
						<option value="普通">普通</option>
						<option value="当座">当座</option>
						<option value="貯蓄">貯蓄</option>
					</select>
				</div>

				<div className="w-full md:w-2/3">
					<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
						口座番号 <span className="text-red-600 dark:text-red-400">*</span>
					</label>
					<input
						type="text"
						name="accountNumber"
						value={formData.accountNumber}
						onChange={(e) => {
							const numericValue = e.target.value.replace(/\D/g, "");
							const event = {
								target: {
									name: "accountNumber",
									value: numericValue,
									type: "text",
								},
							} as React.ChangeEvent<HTMLInputElement>;
							handleChange(event);
						}}
						maxLength={7}
						placeholder="1234567"
						className="w-full px-3 sm:px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400"
						required
					/>
				</div>
			</div>

			{/* Account Holder Name */}
			<div>
				<label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
					口座名義（カタカナ）{" "}
					<span className="text-red-600 dark:text-red-400">*</span>
				</label>
				<input
					type="text"
					name="accountHolderName"
					value={formData.accountHolderName}
					onChange={(e) => {
						handleChange(e);
						setAccountHolderNameError("");
					}}
					placeholder="ヤマダタロウ"
					className={`w-full px-3 sm:px-4 py-2 border ${
						accountHolderNameError
							? "border-red-500 dark:border-red-500"
							: "border-gray-200 dark:border-gray-700"
					} bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400`}
					required
				/>
				{accountHolderNameError ? (
					<p className="mt-1 text-xs text-red-600 dark:text-red-400">
						{accountHolderNameError}
					</p>
				) : (
					<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
						全角カタカナで入力してください
					</p>
				)}
			</div>

			{/* Navigation Buttons */}
			<div className="pt-4 flex justify-between">
				<button
					type="button"
					onClick={prevStep}
					className="w-5/12 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300 py-3 rounded-lg font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
				>
					戻る
				</button>
				<button
					type="button"
					onClick={handleNextStep}
					className="w-5/12 bg-gradient-to-r from-violet-500 to-purple-600 text-white py-3 rounded-lg font-bold hover:from-violet-600 hover:to-purple-700 transition-all"
				>
					次へ
				</button>
			</div>
		</div>
	);
};

export default Step2;
